import * as d3 from 'd3';
import StarIcon from '@mui/icons-material/Star';
import { Alert, Box } from '@mui/material';
import { isFunction, some } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDeviceAlerts } from '@/api/customSensorAlerts/useDeviceAlerts';
import { useDeviceHasSensor } from '@/api/device';
import { CurrentsDatum, useCurrentsData } from '@/api/historicalData';
import { usePreferredTimeZone } from '@/api/userAccount';
import currentIcon from '@/assets/CurrentIcon.svg';
import { LineArea, LineAreaChart, LineDataSlice, defaultDotRenderer, drawLineAreaChart } from '@/charts';
import { colorsets, flattenSensorPositions, linesAcrossSensors } from '@/charts/common';
import { DOT_RADIUS, Line } from '@/charts/lineChart';
import { ColorSet } from '@/charts/types';
import { SensorIcon } from '@/components/data/SensorIcon';
import { ChartSensorSelector } from '@/components/spotter/historicalDataTab/ChartLineSelector';
import {
    ChartDataDetails,
    ChartDataSelectors,
    ChartWithInfo,
    LegendItem,
} from '@/components/spotter/historicalDataTab/ChartWithInfo';
import { CurrentsDatumDetail } from '@/components/spotter/historicalDataTab/datumDetail/CurrentsDatumDetail';
import { DataDetailsByTimestamp } from '@/components/spotter/historicalDataTab/datumDetail/DataDetailPanel';
import {
    DataLineDirectionMean,
    DataLineExtent,
    DataLineMean,
    LineChartDataSummary,
} from '@/components/spotter/historicalDataTab/datumDetail/LineChartDataSummary';
import { CollapsibleSection } from '@/components/ui/CollapsibleSection';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSpotterContext } from '@/context/SpotterContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useChartContainer } from '@/hooks/useChartContainer';
import { useElementSize } from '@/hooks/useElementSize';
import { TrackChartViewWhenRendered } from '@/hooks/useIsViewedTracking';
import { useGlobalStore } from '@/store';
import { alertLineParamsForSensorChart, composeAlertLines, isFiniteNumber } from '@/utils';

const MISSING_DATA_STAR_Y = 160; // half the height of the chart area

export const CURRENT_LINE = {
    valueKey: 'speed',
    unitKey: 'speedUnits',
    unitTypeKey: 'speedUnitType',
    label: 'Current speed',
    renderMissingValueDot: true,
    dotRenderer(params) {
        const { target, line, x } = params;
        // default dot renderer works when speed is defined
        defaultDotRenderer(params);
        // add another renderer for when speed is undefined (overtilt)
        const symbolGenerator = d3
            .symbol()
            .type(d3.symbolStar)
            .size(DOT_RADIUS * 8);
        target
            .append('path')
            .attr('d', symbolGenerator())
            .style('display', (datum) => (isFiniteNumber(datum.speed) ? 'none' : 'initial'))
            .attr('transform', function (...args) {
                return `translate(${isFunction(x) ? x.call(this, ...args) : x},${MISSING_DATA_STAR_Y})`;
            })
            .attr('fill', (line.color as ColorSet).line);
    },
    // placeholders, will be replaced per sensor
    id: 'current',
    color: colorsets.gray,
} satisfies Line<CurrentsDatum>;

export const CURRENT_LINE_WITH_AREA = {
    ...(CURRENT_LINE as unknown as Line<CurrentsChartDatum>),
    yAreaKeyName: 'speedStdDev',
    yAreaFn: (datum: CurrentsDatum) => datum.speedStdDev * 2,
    yAreaLabel: 'Std. deviation',
    color: colorsets.gray,
} satisfies LineConfig;

export type CurrentsChartDatum = CurrentsDatum & {
    sensorPosition: string;
};

type LineConfig = LineArea<CurrentsChartDatum>;

export const CurrentsLineChartSection = () => {
    const chartTitle = 'Currents speed and direction';
    const { spotterId } = useSpotterContext();
    const { hasSensor } = useDeviceHasSensor({ spotterId, type: 'currents' });
    const { dateRange } = useDateRangeContext();
    const timeZone = usePreferredTimeZone();
    const isAlertLinesOn = useGlobalStore((state) => state.isAlertLinesOn);
    const isScaleToFitAlertLinesOn = useGlobalStore((state) => state.isScaleToFitAlertLinesOn);

    const { selectedTimestamp, setSelectedTimestamp } = useTimelineNavigationContext();

    const selectedTimestampRef = useRef(selectedTimestamp);

    selectedTimestampRef.current = selectedTimestamp;
    const [selectedData, setSelectedData] = useState<LineDataSlice<CurrentsChartDatum, LineConfig> | null>(null);
    const [activeSensorPositions, setActiveSensorPositions] = useState<string[]>([]);
    const chartRef = useRef<LineAreaChart<CurrentsChartDatum, LineConfig> | null>(null);

    const {
        data: sensorData,
        isPending,
        isFetching,
    } = useCurrentsData({
        spotterId,
        dateRange,
    });
    const { setContainer, containerRef, containerEl } = useChartContainer();
    const { width, height } = useElementSize(containerRef);
    const { data: deviceAlerts } = useDeviceAlerts({
        config: { enabled: isAlertLinesOn },
    });

    const drawChart = useCallback(drawLineAreaChart, []);

    const { sensorPositions, dataset } = useMemo(() => flattenSensorPositions(sensorData), [sensorData]);

    const alertLines = useMemo(
        () =>
            composeAlertLines({
                deviceAlerts,
                triggerTypes: ['smartmooring_threshold_currents_speed'],
                ...alertLineParamsForSensorChart(sensorPositions, activeSensorPositions),
            }),
        [deviceAlerts, sensorPositions, activeSensorPositions]
    );

    const lines = useMemo(() => linesAcrossSensors([CURRENT_LINE_WITH_AREA], sensorPositions), [sensorPositions]);

    const activeLines = useMemo(
        () => lines.filter((l) => !activeSensorPositions.length || activeSensorPositions.includes(l.sensorPosition)),
        [activeSensorPositions, lines]
    );

    const hasAnyMissingData = useMemo(
        () =>
            some(
                dataset,
                ({ speed, sensorPosition }) => !isFiniteNumber(speed) && some(activeLines, { sensorPosition })
            ),
        [activeLines, dataset]
    );

    useEffect(() => {
        if (!containerEl || isPending) {
            return;
        }

        if (!dataset?.length) {
            return;
        }

        const newChart = drawChart<CurrentsChartDatum, LineConfig>({
            dataset,
            lines: activeLines,
            xAxisKey: 'timestamp',
            yAxisLabel: 'Current speed',
            containerId: 'currents-speed-and-direction-sm-chart',
            showAlertLines: isAlertLinesOn,
            scaleChartToFitAllAlerts: isScaleToFitAlertLinesOn,
            alertLines,
            chartWidth: width,
            chartHeight: height,
            dateRange,
            timeZone,
            onSelectData: setSelectedData,
            onSelectTimestamp: setSelectedTimestamp,
        });
        chartRef.current = newChart;
        newChart.setSelectedTimestamp?.(selectedTimestampRef.current ? new Date(selectedTimestampRef.current) : null);

        return () => {
            newChart.destroy();
            if (chartRef.current === newChart) {
                chartRef.current = null;
            }
        };
    }, [
        activeLines,
        containerEl,
        dataset,
        drawChart,
        height,
        isAlertLinesOn,
        isPending,
        width,
        dateRange,
        timeZone,
        isScaleToFitAlertLinesOn,
        alertLines,
        setSelectedTimestamp,
    ]);

    useEffect(() => {
        chartRef.current?.setSelectedTimestamp?.(selectedTimestamp ? new Date(selectedTimestamp) : null);
    }, [selectedTimestamp]);

    const hasData = Object.values(sensorData ?? {}).some((data) => data?.length > 0);

    if (!hasSensor && !hasData) {
        // no data in this window and no evidence that we're supposed to have the sensor; disappear
        return false;
    }

    return (
        <CollapsibleSection
            isFetching={isFetching}
            isPending={isPending}
            title={chartTitle}
            icon={<SensorIcon src={currentIcon} />}
        >
            {hasData && dataset ? (
                <>
                    <TrackChartViewWhenRendered
                        containerEl={containerEl}
                        chartTitle={chartTitle}
                        spotterId={spotterId}
                    />
                    <ChartWithInfo
                        legend={
                            hasAnyMissingData && (
                                <LegendItem icon={<StarIcon sx={{ fontSize: '12px' }} />}>Incomplete data</LegendItem>
                            )
                        }
                        chart={
                            <Box ref={setContainer} id="currents-speed-and-direction-sm-chart" width={1} height={1} />
                        }
                    >
                        <ChartDataSelectors>
                            <ChartSensorSelector
                                sensorPositions={sensorPositions}
                                onChange={setActiveSensorPositions}
                            />
                        </ChartDataSelectors>
                        <ChartDataDetails>
                            {!selectedData && (
                                <LineChartDataSummary
                                    dateRange={dateRange}
                                    lines={activeLines}
                                    dataset={dataset}
                                    renderLineSummary={() => (
                                        <>
                                            <DataLineMean />
                                            <DataLineExtent />
                                            <DataLineDirectionMean
                                                label="Averaged current direction"
                                                valueKey="direction"
                                                unitKey="directionUnits"
                                                type="flow"
                                            />
                                        </>
                                    )}
                                />
                            )}
                            <DataDetailsByTimestamp
                                selectedData={selectedData}
                                datumRenderer={({ lines, datum }) =>
                                    lines.map((line) => (
                                        <CurrentsDatumDetail
                                            key={line.id}
                                            datum={datum}
                                            dataset={dataset}
                                            line={line}
                                        />
                                    ))
                                }
                            />
                        </ChartDataDetails>
                    </ChartWithInfo>
                </>
            ) : (
                <Alert severity="info">No currents data for selected date range</Alert>
            )}
        </CollapsibleSection>
    );
};
